import { ProctoringOption } from "@verity/proctoring-option/types";
import { CategoryOptionsListProps, OptionProps } from "../proctoringOptionsGrid";
import { shouldShowProctoringOption } from "../configurationSummaryList";
import UrlAllowBlockLists from "../option-modals/urlAllowBlockLists";
import { useState } from "react";
import OptionBlock from "../optionBlock";
import { getUnsupportedToggleText, PROCTORING_OPTION_TEXT } from "../option-text";
import i18nPackage from "../../../i18n/i18n";
import { CoreButton } from "../../../core";
import { IncognitoWarningModal } from "../option-modals/incognito-warning-modal";
import styles from "./index.module.css";

function DisableNewTabOption<T extends Partial<ProctoringOption>>({
	option,
	updateOption,
	disabled,
	isOptionAllowed,
	horizontalOrientation,
	getPartiallySupportedPlatforms,
}: OptionProps<T>) {
	const { closeOpenTabs, disableNewTab, isExtension } = option;
	const [warningModalShowing, setWarningModalShowing] = useState(false);

	const notAllowed = !isOptionAllowed("disableNewTab");
	return (
		<>
			<OptionBlock
				name="disableNewTab"
				checked={Boolean(disableNewTab)}
				updateState={(checked) => {
					updateOption({
						...option,
						disableNewTab: checked,
						...(closeOpenTabs || checked ? {} : { tabWhiteList: [] }),
					});
					if (checked && isExtension) {
						setWarningModalShowing(true);
					}
				}}
				description={PROCTORING_OPTION_TEXT.disableNewTab.description}
				Icon={PROCTORING_OPTION_TEXT.disableNewTab.icon}
				contentHeader={PROCTORING_OPTION_TEXT.disableNewTab.header}
				disabled={notAllowed}
				fullDisabled={disabled || notAllowed}
				disabledMessage={
					notAllowed
						? i18nPackage.t("editQuizPage.configurationStep.proctoringOptionsGridTSX.instituteDisabled")
						: getUnsupportedToggleText(option, "disableNewTab", !disableNewTab, Boolean(disableNewTab))
				}
				horizontalOrientation={horizontalOrientation}
				partiallySupportedPlatforms={getPartiallySupportedPlatforms?.("disableNewTab")}
			/>
			{warningModalShowing && (
				<IncognitoWarningModal
					description={i18nPackage.t("editQuizPage.configurationStep.incognitoModal.description.disableNewTabs")}
					open={warningModalShowing}
					onClose={() => setWarningModalShowing(false)}
				/>
			)}
		</>
	);
}

function CloseOpenTabsOption<T extends Partial<ProctoringOption>>({
	option,
	updateOption,
	disabled,
	isOptionAllowed,
	horizontalOrientation,
	getPartiallySupportedPlatforms,
}: OptionProps<T>) {
	const { closeOpenTabs, disableNewTab, isExtension } = option;
	const [warningModalShowing, setWarningModalShowing] = useState(false);

	const notAllowed = !isOptionAllowed("closeOpenTabs");
	return (
		<>
			<OptionBlock
				name="closeOpenTabs"
				checked={Boolean(closeOpenTabs)}
				updateState={(checked) => {
					updateOption({
						...option,
						closeOpenTabs: checked,
						...(checked || disableNewTab ? {} : { tabWhiteList: [] }),
					});
					if (checked && isExtension) {
						setWarningModalShowing(true);
					}
				}}
				description={PROCTORING_OPTION_TEXT.closeOpenTabs.description}
				Icon={PROCTORING_OPTION_TEXT.closeOpenTabs.icon}
				contentHeader={PROCTORING_OPTION_TEXT.closeOpenTabs.header}
				disabled={notAllowed}
				fullDisabled={disabled || notAllowed}
				disabledMessage={
					notAllowed
						? i18nPackage.t("editQuizPage.configurationStep.proctoringOptionsGridTSX.instituteDisabled")
						: getUnsupportedToggleText(option, "closeOpenTabs", !closeOpenTabs, Boolean(closeOpenTabs))
				}
				horizontalOrientation={horizontalOrientation}
				partiallySupportedPlatforms={getPartiallySupportedPlatforms?.("closeOpenTabs")}
			/>
			{warningModalShowing && (
				<IncognitoWarningModal
					description={i18nPackage.t("editQuizPage.configurationStep.incognitoModal.description.closeOpenTabs")}
					open={warningModalShowing}
					onClose={() => setWarningModalShowing(false)}
				/>
			)}
		</>
	);
}

function DisableWebsitesOption<T extends Partial<ProctoringOption>>({
	option,
	updateOption,
	disabled,
	isOptionAllowed,
	horizontalOrientation,
	getPartiallySupportedPlatforms,
}: OptionProps<T>) {
	const { closeOpenTabs, disableNewTab, tabWhiteList } = option;
	const [blockListChecked, setBlockListChecked] = useState(
		Boolean(closeOpenTabs || disableNewTab) && (tabWhiteList?.length ?? 0) > 0,
	);
	const [blockListModalShowing, setBlockListModalShowing] = useState(false);

	const notAllowed = !isOptionAllowed("tabWhiteList");
	return (
		<>
			<OptionBlock
				name="websiteAccess"
				checked={blockListChecked && Boolean(closeOpenTabs || disableNewTab)}
				updateState={(checked) => {
					setBlockListChecked(checked && Boolean(closeOpenTabs || disableNewTab));
					if (checked) {
						setBlockListModalShowing(true);
					} else {
						updateOption({
							...option,
							tabWhiteList: [],
						});
					}
				}}
				description={PROCTORING_OPTION_TEXT.tabWhiteList.description}
				Icon={PROCTORING_OPTION_TEXT.tabWhiteList.icon}
				contentHeader={PROCTORING_OPTION_TEXT.tabWhiteList.header}
				disabled={notAllowed || (!closeOpenTabs && !disableNewTab)}
				fullDisabled={disabled || notAllowed}
				disabledMessage={
					notAllowed
						? i18nPackage.t("editQuizPage.configurationStep.proctoringOptionsGridTSX.instituteDisabled")
						: !closeOpenTabs && !disableNewTab
							? `Please enable the restrictions to Disable New Tabs, Close Open Tabs, or both.`
							: getUnsupportedToggleText(option, "tabWhiteList", tabWhiteList, blockListModalShowing)
				}
				menuItems={
					<CoreButton className={styles.openModalButton} onClick={() => setBlockListModalShowing(true)}>
						{i18nPackage.t("editQuizPage.configurationStep.tabOptionsTSX.tabWhiteList")}
					</CoreButton>
				}
				horizontalOrientation={horizontalOrientation}
				partiallySupportedPlatforms={getPartiallySupportedPlatforms?.("tabWhiteList")}
			/>
			{blockListModalShowing && (
				<UrlAllowBlockLists
					onClose={() => setBlockListModalShowing(false)}
					currOption={option}
					updateOption={updateOption}
				/>
			)}
		</>
	);
}

export default function TabsOptions<T extends Partial<ProctoringOption>>({
	option,
	updateOption,
	disabled,
	isOptionAllowed,
	horizontalOrientation = false,
	getPartiallySupportedPlatforms,
}: CategoryOptionsListProps<T>) {
	return (
		<div className={styles.proctorSettingsGrid}>
			{shouldShowProctoringOption("disableNewTab") && (
				<DisableNewTabOption
					option={option}
					updateOption={updateOption}
					disabled={disabled}
					isOptionAllowed={isOptionAllowed}
					horizontalOrientation={horizontalOrientation}
					getPartiallySupportedPlatforms={getPartiallySupportedPlatforms}
				/>
			)}
			{shouldShowProctoringOption("closeOpenTabs") && (
				<CloseOpenTabsOption
					option={option}
					updateOption={updateOption}
					disabled={disabled}
					isOptionAllowed={isOptionAllowed}
					horizontalOrientation={horizontalOrientation}
					getPartiallySupportedPlatforms={getPartiallySupportedPlatforms}
				/>
			)}
			{shouldShowProctoringOption("tabWhiteList") && (
				<DisableWebsitesOption
					option={option}
					updateOption={updateOption}
					disabled={disabled}
					isOptionAllowed={isOptionAllowed}
					horizontalOrientation={horizontalOrientation}
					getPartiallySupportedPlatforms={getPartiallySupportedPlatforms}
				/>
			)}
		</div>
	);
}
